import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Login } from "./pages/Login.js";
import { Dashboard } from "./pages/Dashboard.js";
import { AppLayout } from "./layouts/AppLayout.js";
import { AuthLayout } from "./layouts/AuthLayout.js";
import { FullscreenLayout } from "./layouts/FullscreenLayout.js";
import { RouteErrorBoundary } from "./components/app/RouteErrorBoundary.js";
import { ConfirmProvider } from "./components/app/ConfirmProvider.js";
import { getUser, isLoggedIn } from "./auth.js";
import { canManageSlips, hasRole } from "./components/app/roles.js";

// Lazy-load everything else. The exam-taking path, authoring (teacher-only), and
// platform admin (super_admin-only) are large page trees; they don't need to
// download on first paint for every user. Dashboard and Login stay eager so the
// shell feels instant.
const TestRunner = lazy(() => import("./pages/TestRunner.js").then((m) => ({ default: m.TestRunner })));
const Result = lazy(() => import("./pages/Result.js").then((m) => ({ default: m.Result })));
const StudentProgress = lazy(() =>
  import("./pages/StudentProgress.js").then((m) => ({ default: m.StudentProgress })),
);
const Stats = lazy(() => import("./pages/Stats.js").then((m) => ({ default: m.Stats })));
const MarkingQueue = lazy(() =>
  import("./pages/MarkingQueue.js").then((m) => ({ default: m.MarkingQueue })),
);
const MarkingAttempt = lazy(() =>
  import("./pages/MarkingAttempt.js").then((m) => ({ default: m.MarkingAttempt })),
);
const ModerationResolve = lazy(() =>
  import("./pages/ModerationResolve.js").then((m) => ({ default: m.ModerationResolve })),
);
const AuthoringList = lazy(() =>
  import("./pages/authoring/AuthoringList.js").then((m) => ({ default: m.AuthoringList })),
);
const AuthoringEditor = lazy(() =>
  import("./pages/authoring/AuthoringEditor.js").then((m) => ({ default: m.AuthoringEditor })),
);
const SittingsList = lazy(() =>
  import("./pages/SittingsList.js").then((m) => ({ default: m.SittingsList })),
);
const SittingEditor = lazy(() =>
  import("./pages/SittingEditor.js").then((m) => ({ default: m.SittingEditor })),
);
const SittingHub = lazy(() =>
  import("./pages/SittingHub.js").then((m) => ({ default: m.SittingHub })),
);
const SittingResultPage = lazy(() =>
  import("./pages/SittingResult.js").then((m) => ({ default: m.SittingResult })),
);
const SittingMonitorPage = lazy(() =>
  import("./pages/SittingMonitor.js").then((m) => ({
    default: m.SittingMonitor,
  })),
);
const OrgProgrammeTemplatePage = lazy(() =>
  import("./pages/OrgProgrammeTemplate.js").then((m) => ({
    default: m.OrgProgrammeTemplate,
  })),
);
const PlatformTenants = lazy(() =>
  import("./pages/platform/PlatformTenants.js").then((m) => ({ default: m.PlatformTenants })),
);
const PlatformTenantDetailPage = lazy(() =>
  import("./pages/platform/PlatformTenantDetail.js").then((m) => ({
    default: m.PlatformTenantDetailPage,
  })),
);
const PlatformMetricsPage = lazy(() =>
  import("./pages/platform/PlatformMetrics.js").then((m) => ({
    default: m.PlatformMetricsPage,
  })),
);
const PlatformQuestionsPage = lazy(() =>
  import("./pages/platform/PlatformQuestions.js").then((m) => ({
    default: m.PlatformQuestionsPage,
  })),
);
const PlatformQuestionReviewQueuePage = lazy(() =>
  import("./pages/platform/PlatformQuestionReviewQueue.js").then((m) => ({
    default: m.PlatformQuestionReviewQueuePage,
  })),
);
const PlatformQuestionModerationQueuePage = lazy(() =>
  import("./pages/platform/PlatformQuestionModerationQueue.js").then((m) => ({
    default: m.PlatformQuestionModerationQueuePage,
  })),
);
const PlatformQuestionEditorPage = lazy(() =>
  import("./pages/platform/PlatformQuestionEditor.js").then((m) => ({
    default: m.PlatformQuestionEditorPage,
  })),
);
const PlatformQuestionPreviewPage = lazy(() =>
  import("./pages/platform/PlatformQuestionPreview.js").then((m) => ({
    default: m.PlatformQuestionPreviewPage,
  })),
);
const PlatformSetEditorPage = lazy(() =>
  import("./pages/platform/PlatformSetEditor.js").then((m) => ({
    default: m.PlatformSetEditorPage,
  })),
);
const PlatformProgrammesPage = lazy(() =>
  import("./pages/platform/PlatformProgrammes.js").then((m) => ({
    default: m.PlatformProgrammesPage,
  })),
);
const PlatformAuditPage = lazy(() =>
  import("./pages/platform/PlatformAudit.js").then((m) => ({
    default: m.PlatformAuditPage,
  })),
);
const PlatformBillingPage = lazy(() =>
  import("./pages/platform/PlatformBilling.js").then((m) => ({
    default: m.PlatformBillingPage,
  })),
);
const PlatformErrorsPage = lazy(() =>
  import("./pages/platform/PlatformErrors.js").then((m) => ({
    default: m.PlatformErrors,
  })),
);
const PlatformDiagnosticsPage = lazy(() =>
  import("./pages/platform/PlatformDiagnostics.js").then((m) => ({
    default: m.PlatformDiagnosticsPage,
  })),
);
const PlatformBrandingPage = lazy(() =>
  import("./pages/platform/PlatformBranding.js").then((m) => ({
    default: m.PlatformBrandingPage,
  })),
);
const PlatformEmailsPage = lazy(() =>
  import("./pages/platform/PlatformEmails.js").then((m) => ({
    default: m.PlatformEmailsPage,
  })),
);
const OrgMembersPage = lazy(() =>
  import("./pages/OrgMembers.js").then((m) => ({ default: m.OrgMembersPage })),
);
const RoomCodesPage = lazy(() =>
  import("./pages/org/RoomCodes.js").then((m) => ({
    default: m.RoomCodes,
  })),
);
const CandidateSlipsPage = lazy(() =>
  import("./pages/CandidateSlipsPage.js").then((m) => ({
    default: m.CandidateSlipsPage,
  })),
);
const SessionsPage = lazy(() =>
  import("./pages/Sessions.js").then((m) => ({ default: m.SessionsPage })),
);
const InviteAccept = lazy(() =>
  import("./pages/InviteAccept.js").then((m) => ({ default: m.InviteAccept })),
);
const PasswordReset = lazy(() =>
  import("./pages/PasswordReset.js").then((m) => ({ default: m.PasswordReset })),
);
const ForgotPassword = lazy(() =>
  import("./pages/ForgotPassword.js").then((m) => ({ default: m.ForgotPassword })),
);
const CandidateEntry = lazy(() =>
  import("./pages/CandidateEntry.js").then((m) => ({ default: m.CandidateEntry })),
);
const Landing = lazy(() =>
  import("./pages/Landing.js").then((m) => ({ default: m.Landing })),
);
const CandidateSlipPrint = lazy(() =>
  import("./pages/org/CandidateSlipPrint.js").then((m) => ({
    default: m.CandidateSlipPrint,
  })),
);
const Privacy = lazy(() =>
  import("./pages/legal/Privacy.js").then((m) => ({ default: m.Privacy })),
);
const Terms = lazy(() =>
  import("./pages/legal/Terms.js").then((m) => ({ default: m.Terms })),
);
const Cookies = lazy(() =>
  import("./pages/legal/Cookies.js").then((m) => ({ default: m.Cookies })),
);
const DPA = lazy(() =>
  import("./pages/legal/DPA.js").then((m) => ({ default: m.DPA })),
);
const Trust = lazy(() =>
  import("./pages/legal/Trust.js").then((m) => ({ default: m.Trust })),
);
const Pricing = lazy(() =>
  import("./pages/Pricing.js").then((m) => ({ default: m.Pricing })),
);
const IntakeForm = lazy(() =>
  import("./pages/IntakeForm.js").then((m) => ({ default: m.IntakeForm })),
);
const Verify = lazy(() =>
  import("./pages/Verify.js").then((m) => ({ default: m.Verify })),
);
const Status = lazy(() =>
  import("./pages/legal/Status.js").then((m) => ({ default: m.Status })),
);
const EmailChangeConfirm = lazy(() =>
  import("./pages/EmailChangeConfirm.js").then((m) => ({
    default: m.EmailChangeConfirm,
  })),
);
const VerifyEmail = lazy(() =>
  import("./pages/VerifyEmail.js").then((m) => ({ default: m.VerifyEmail })),
);
const SettingsSecurityPage = lazy(() =>
  import("./pages/SettingsSecurity.js").then((m) => ({
    default: m.SettingsSecurityPage,
  })),
);
const SettingsProfilePage = lazy(() =>
  import("./pages/SettingsProfile.js").then((m) => ({
    default: m.SettingsProfilePage,
  })),
);
const OrgSettingsPage = lazy(() =>
  import("./pages/OrgSettings.js").then((m) => ({ default: m.OrgSettingsPage })),
);
const OrgAuditPage = lazy(() =>
  import("./pages/org/OrgAudit.js").then((m) => ({ default: m.OrgAudit })),
);
const BillingPage = lazy(() => import("./pages/org/Billing.js"));
const CentreDocumentationPage = lazy(() => import("./pages/org/CentreDocumentation.js"));
const IntakeApplyPage = lazy(() => import("./pages/org/IntakeApply.js"));
const HelpPage = lazy(() => import("./pages/Help.js"));
const SubmissionsPage = lazy(() => import("./pages/org/Submissions.js"));
const SubmissionDetailPage = lazy(() => import("./pages/org/SubmissionDetail.js"));
const ReplaysPage = lazy(() => import("./pages/org/Replays.js"));
const ReplayDetailPage = lazy(() => import("./pages/org/ReplayDetail.js"));
const AuthoringPreview = lazy(() =>
  import("./pages/authoring/AuthoringPreview.js").then((m) => ({
    default: m.AuthoringPreview,
  })),
);
const MarkingWorkloadPage = lazy(() =>
  import("./pages/MarkingWorkload.js").then((m) => ({
    default: m.MarkingWorkloadPage,
  })),
);
const PlatformDeletionRequestsPage = lazy(() =>
  import("./pages/platform/PlatformDeletionRequests.js").then((m) => ({
    default: m.PlatformDeletionRequestsPage,
  })),
);
const Accessibility = lazy(() =>
  import("./pages/Accessibility.js").then((m) => ({ default: m.Accessibility })),
);

function RouteLoading() {
  return (
    <div className="flex min-h-40 items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
    </div>
  );
}

export function App() {
  const location = useLocation();
  return (
    <RouteErrorBoundary pathname={location.pathname}>
      <ConfirmProvider>
      <Suspense fallback={<RouteLoading />}>
        <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/accessibility" element={<Accessibility />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/dpa" element={<DPA />} />
        <Route path="/trust" element={<Trust />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/verify" element={<Verify />} />
        <Route path="/verify/:certNo" element={<Verify />} />
        <Route path="/status" element={<Status />} />
        {/* Public pre-onboarding intake. Deliberately OUTSIDE AuthLayout: the
            person filling it in has no account, so sign-in chrome would be
            confusing and the layout offers nothing they can use. */}
        <Route path="/intake/:token" element={<IntakeForm />} />

        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/enter" element={<CandidateEntry />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/reset-password/:token" element={<PasswordReset />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route
            path="/settings/email-change/:token"
            element={<EmailChangeConfirm />}
          />
        </Route>

        <Route element={<FullscreenLayout />}>
          <Route path="/attempts/:attemptId/run" element={<TestRunner />} />
        </Route>

        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/:slug" element={<HelpPage />} />
          <Route path="/attempts/:attemptId/result" element={<Result />} />
          <Route
            path="/progress"
            element={
              <ExactRole role="student">
                <StudentProgress />
              </ExactRole>
            }
          />
          <Route
            path="/stats"
            element={
              <RoleGated minRole="teacher">
                <Stats
                  isAdmin={getUser()?.role === "tenant_admin" || getUser()?.role === "super_admin"}
                />
              </RoleGated>
            }
          />
          <Route
            path="/marking"
            element={
              <ExaminerRoute>
                <MarkingQueue />
              </ExaminerRoute>
            }
          />
          <Route
            path="/marking/:attemptId"
            element={
              <ExaminerRoute>
                <MarkingAttempt />
              </ExaminerRoute>
            }
          />
          <Route
            path="/marking/:attemptId/moderate"
            element={
              <RoleGated minRole="moderator">
                <ModerationResolve />
              </RoleGated>
            }
          />
          <Route
            path="/authoring"
            element={
              <RoleGated minRole="teacher">
                <AuthoringList />
              </RoleGated>
            }
          />
          <Route
            path="/authoring/:assessmentId"
            element={
              <RoleGated minRole="teacher">
                <AuthoringEditor />
              </RoleGated>
            }
          />
          <Route
            path="/authoring/:assessmentId/preview"
            element={
              <RoleGated minRole="teacher">
                <AuthoringPreview />
              </RoleGated>
            }
          />
          <Route
            path="/sittings"
            element={
              <RoleGated minRole="teacher">
                <SittingsList />
              </RoleGated>
            }
          />
          <Route
            path="/sittings/new"
            element={
              <RoleGated minRole="teacher">
                <SittingEditor />
              </RoleGated>
            }
          />
          <Route
            path="/sittings/:id"
            element={
              <RoleGated minRole="teacher">
                <SittingEditor />
              </RoleGated>
            }
          />
          <Route
            path="/sittings/:sittingId/overview"
            element={<SittingHub />}
          />
          <Route
            path="/sittings/:sittingId/preview-overview"
            element={
              <RoleGated minRole="teacher">
                <SittingHub preview />
              </RoleGated>
            }
          />
          <Route
            path="/sittings/:sittingId/monitor"
            element={
              <RoleGated minRole="teacher">
                <SittingMonitorPage />
              </RoleGated>
            }
          />
          <Route
            path="/sittings/:id/result"
            element={
              <ExactRole role="student">
                <SittingResultPage />
              </ExactRole>
            }
          />
          <Route
            path="/platform"
            element={
              <RoleGated minRole="super_admin">
                <PlatformTenants />
              </RoleGated>
            }
          />
          <Route
            path="/platform/tenants/:tenantId"
            element={
              <RoleGated minRole="super_admin">
                <PlatformTenantDetailPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/metrics"
            element={
              <RoleGated minRole="super_admin">
                <PlatformMetricsPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/branding"
            element={
              <RoleGated minRole="super_admin">
                <PlatformBrandingPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/emails"
            element={
              <RoleGated minRole="super_admin">
                <PlatformEmailsPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/questions"
            element={
              <RoleGated minRole="super_admin">
                <PlatformQuestionsPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/questions/review-queue"
            element={
              <RoleGated minRole="super_admin">
                <PlatformQuestionReviewQueuePage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/questions/moderation"
            element={
              <RoleGated minRole="super_admin">
                <PlatformQuestionModerationQueuePage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/questions/:id/edit"
            element={
              <RoleGated minRole="super_admin">
                <PlatformQuestionEditorPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/questions/:id"
            element={
              <RoleGated minRole="super_admin">
                <PlatformQuestionPreviewPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/sets/new"
            element={
              <RoleGated minRole="super_admin">
                <PlatformSetEditorPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/sets/:id"
            element={
              <RoleGated minRole="super_admin">
                <PlatformSetEditorPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/programmes"
            element={
              <RoleGated minRole="super_admin">
                <PlatformProgrammesPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/audit"
            element={
              <RoleGated minRole="super_admin">
                <PlatformAuditPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/billing"
            element={
              <RoleGated minRole="super_admin">
                <PlatformBillingPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/errors"
            element={
              <RoleGated minRole="super_admin">
                <PlatformErrorsPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/diagnostics"
            element={
              <RoleGated minRole="super_admin">
                <PlatformDiagnosticsPage />
              </RoleGated>
            }
          />
          <Route
            path="/platform/deletion-requests"
            element={
              <RoleGated minRole="super_admin">
                <PlatformDeletionRequestsPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/marking-workload"
            element={
              <RoleGated minRole="tenant_admin">
                <MarkingWorkloadPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/members"
            element={
              <RoleGated minRole="tenant_admin">
                <OrgMembersPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/candidate-slips"
            element={
              <SlipManagerGated>
                <CandidateSlipsPage />
              </SlipManagerGated>
            }
          />
          <Route
            path="/org/room-codes"
            element={
              <SlipManagerGated>
                <RoomCodesPage />
              </SlipManagerGated>
            }
          />
          <Route
            path="/org/candidate-slips/:id/print"
            element={
              <SlipManagerGated>
                <CandidateSlipPrint />
              </SlipManagerGated>
            }
          />
          <Route path="/settings/profile" element={<SettingsProfilePage />} />
          <Route path="/settings/sessions" element={<SessionsPage />} />
          <Route path="/settings/security" element={<SettingsSecurityPage />} />
          {/* /billing/success and /billing/cancel are the Stripe Checkout return
              URLs (BILLING_SUCCESS_URL / BILLING_CANCEL_URL). They matched no
              route before, so the `*` catch-all bounced a tenant who had just
              paid to the public marketing page with no confirmation. */}
          <Route
            path="/billing"
            element={
              <RoleGated minRole="tenant_admin">
                <BillingPage />
              </RoleGated>
            }
          />
          <Route
            path="/billing/success"
            element={
              <RoleGated minRole="tenant_admin">
                <BillingPage />
              </RoleGated>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <RoleGated minRole="tenant_admin">
                <BillingPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/settings"
            element={
              <RoleGated minRole="tenant_admin">
                <OrgSettingsPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/programmes/:programmeId/transcript-template"
            element={
              <RoleGated minRole="tenant_admin">
                <OrgProgrammeTemplatePage />
              </RoleGated>
            }
          />
          <Route
            path="/org/audit"
            element={
              <RoleGated minRole="tenant_admin">
                <OrgAuditPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/centre-documentation"
            element={
              <RoleGated minRole="tenant_admin">
                <CentreDocumentationPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/intake"
            element={
              <RoleGated minRole="tenant_admin">
                <IntakeApplyPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/submissions"
            element={
              <RoleGated minRole="moderator">
                <SubmissionsPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/submissions/:attemptId"
            element={
              <RoleGated minRole="moderator">
                <SubmissionDetailPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/replays"
            element={
              <RoleGated minRole="tenant_admin">
                <ReplaysPage />
              </RoleGated>
            }
          />
          <Route
            path="/org/replays/:attemptId"
            element={
              <RoleGated minRole="tenant_admin">
                <ReplayDetailPage />
              </RoleGated>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      </ConfirmProvider>
    </RouteErrorBoundary>
  );
}

function HomeRedirect() {
  return isLoggedIn() ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function RoleGated({ minRole, children }: { minRole: string; children: JSX.Element }) {
  const role = getUser()?.role ?? "student";
  if (!hasRole(role, minRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function ExactRole({ role, children }: { role: string; children: JSX.Element }) {
  const actual = getUser()?.role ?? "student";
  if (actual !== role) return <Navigate to="/dashboard" replace />;
  return children;
}

// Slip-management gate: `registrar` is non-hierarchical (doesn't outrank
// teacher/moderator), so RoleGated's min-rank check would either let
// everyone above student in or no registrar at all. Mirrors the API's
// requireSlipManager() allowlist exactly.
function SlipManagerGated({ children }: { children: JSX.Element }) {
  const role = getUser()?.role ?? "student";
  if (!canManageSlips(role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Gate for marking routes. Matches the API's requireExaminer middleware
// exactly: the caller needs the `is_examiner` flag on their membership,
// OR be a moderator (for whom the flag is implicit by role). Tenant admins
// and super admins do NOT auto-qualify — they impersonate an examiner
// user, or a tenant admin marks them as examiner, before they can mark.
// This keeps the 403 out of the console for typical super-admin logins.
function ExaminerRoute({ children }: { children: JSX.Element }) {
  const user = getUser();
  const role = user?.role ?? "student";
  const isExaminer = !!user?.is_examiner || role === "moderator";
  if (!isExaminer) return <Navigate to="/dashboard" replace />;
  return children;
}
